import { Alert, Skeleton, theme } from "antd"
import React, { Suspense, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { LazyPdfPages } from "../../../components/Esign/index.ts"
import { bytesOf } from "../../../components/Esign/signing/bytes.ts"
import { fetchOwnFields } from "../api/signing.services"
import type { OwnFields } from "../types/signing.types"

interface Props {
  session: string
  documentBlob: Blob | null
  height: string
  /** Before consent the reader holds no fields yet, so the pages are drawn bare. */
  withFields?: boolean
}

type MarkedDocument = { bytes: Uint8Array; placement: OwnFields | null }

// Bytes pdf.js cannot draw are never embedded: the browser would render them as a document of this origin.
const SigningDocumentView: React.FC<Props> = ({
  session,
  documentBlob,
  height,
  withFields = true
}) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const [marked, setMarked] = useState<MarkedDocument | null>(null)
  const [unavailable, setUnavailable] = useState(!documentBlob)

  useEffect(() => {
    if (!documentBlob) {
      setUnavailable(true)
      return
    }
    let abandoned = false
    setMarked(null)
    setUnavailable(false)
    Promise.all([
      bytesOf(documentBlob),
      withFields ? fetchOwnFields(session) : null
    ])
      .then(([bytes, response]) => {
        if (abandoned) return
        const placement = response?.ok && response.data ? response.data : null
        setMarked({ bytes, placement })
      })
      .catch(() => {
        if (!abandoned) setUnavailable(true)
      })
    return () => {
      abandoned = true
    }
  }, [documentBlob, session, withFields])

  if (unavailable) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t("signing.document.cannotDisplay")}
      />
    )
  }

  if (!marked) {
    return (
      <div aria-busy aria-label={t("esign.pages.loading")}>
        <Skeleton.Node active className="!w-full" style={{ height }} />
      </div>
    )
  }

  return (
    <div
      className="overflow-auto rounded-lg p-2"
      style={{
        maxHeight: height,
        background: token.colorFillTertiary,
        border: `1px solid ${token.colorBorderSecondary}`
      }}
    >
      <Suspense fallback={<Skeleton active paragraph={{ rows: 8 }} />}>
        <LazyPdfPages
          data={marked.bytes}
          pages={marked.placement?.pages}
          onError={() => setUnavailable(true)}
        />
      </Suspense>
    </div>
  )
}

export default SigningDocumentView
